<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BSidebar
      id="sbRsmCustomer"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>

      <BContainer>
        <BRow>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Cluster"
              label-for="filterCluster"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="tableCustomers.selected.cluster"
                placeholder="filter cluster here"
                input-id="filterCluster"
                label="cluster_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(tableCustomers.options.clusters)"
                :loading="(tableCustomers.fetching.clusters)"
                :disabled="(tableCustomers.busy || tableCustomers.fetching.clusters)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterClusters"
              >
                <template #no-options="">
                  no available cluster
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="tableCustomers.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbRsmCustomer
                variant="primary"
              >
                <i
                  v-if="[
                    settingFilters,
                    tableCustomers.fetching.clusters
                  ].some(isTrue => isTrue)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableCustomers"
      :provider="tableProvider"
      :fields="tableCustomers.fields"
      :filter="tableCustomers.filter"
      :filter-on="tableCustomers.filterOn"
      :sort-by="tableCustomers.sortBy"
      :sort-desc="tableCustomers.sortDesc"
      :sort-direction="tableCustomers.sortDirection"
      :total-rows="tableCustomers.totalRows"
      :current-page="tableCustomers.currentPage"
      :per-page="tableCustomers.perPage"
      :stacked="tableCustomers.stacked"
      :busy="tableCustomers.busy"
    >
      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            variant="primary"
            :disabled="tableCustomers.busy"
            :to="{
              name: 'asm.customers.profile',
              params: {
                customer: item.id
              }
            }"
          >
            Manage
          </BButton>
        </div>
      </template>

      <template #cell(customer_name)="{ item }">
        <div class="d-flex flex-column gap-1 p-1">
          <div class="font-weight-bolder">
            {{ item.customer_name }}
          </div>
          <div style="font-size: 10px;">
            {{ item.address }}
          </div>
        </div>
      </template>
    </CustomTable>

  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import { mapGetters } from 'vuex'
import { setTableOptions, getTableOptions } from '@/forage/asm/customers'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MClusters from '@/mixins/locations/clusters'

import { SASMCustomer } from '@/services/asm'

export default {

  name: 'VASMCustomers',

  mixins: [
    MClusters,
    MxMisc,
    MxFormatter,
    MxImageUrl
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.tableCustomers.selected.area = {
            id: context.currentUser?.area || ''
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.tableCustomers = Object.assign(context.tableCustomers, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 0,
              filter: {
                search: filter?.search || '',
                cluster: ''
              },
              editable: {
                cluster: filter?.cluster || ''
              }
            })
          } else {
            context.tableCustomers.currentPage = 1
          }

          context.$nextTick(() => {
            context.getClusters(context.tableCustomers, '', context.currentUser.area).finally(() => {
              if (context.tableCustomers.editable.cluster) {
                context.tableCustomers.selected.cluster = QUERY.find(context.tableCustomers.options.clusters, {
                  id: context.tableCustomers.editable.cluster
                }) || ''

                context.tableCustomers.editable.cluster = ''
              }
            })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'asm'
  ],

  metaInfo: () => ({
    title: 'Customers'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        settings: false
      },
      tableCustomers: {
        busy: false,
        fetching: {
          clusters: false
        },
        options: {
          yesOrNos: [
            { value: 'All', text: 'All' },
            { value: 1, text: 'Yes' },
            { value: 0, text: 'No' }
          ],
          clusters: []
        },
        editable: {
          cluster: ''
        },
        selected: {
          tagging: 1,
          area: {
            id: ''
          },
          cluster: ''
        },
        filter: {
          search: '',
          cluster: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 0,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-right stacked-full text-md-center' },
          { mobile: 7, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'customer_name', label: 'Customer', class: 'min-w-md-250px', formatter: this.getFullName, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    settingFilters () {
      const hasEditables = Object.keys(this.tableCustomers.editable).map(
        editableKey => this.tableCustomers.editable[editableKey]
      ).some(val => String(val) !== '')

      return hasEditables
    }
  },

  watch: {
    'tableCustomers.selected.cluster' (cluster) {
      this.tableCustomers.filter.cluster = cluster?.id || ''
    }
  },

  methods: {
    customerStatus (status) {
      switch (status) {
        case 'Verified': {
          return 'secondary'
        }
        case 'New': {
          return 'primary'
        }
        default: {
          return 'warning'
        }
      }
    },

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      if (this.settingFilters) {
        return []
      }

      this.tableCustomers.busy = true

      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search,
        area_id: this.currentUser.area,
        cluster_id: filter.cluster
      }

      return await SASMCustomer.get(payload).then(
        ({ data }) => {
          this.tableCustomers.totalRows = data.total_rows
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: data.total_rows
          })
          return data.items
        }
      ).catch(
        () => {
          this.tableCustomers.totalRows = 0
          setTableOptions({
            currentPage,
            perPage,
            sortBy,
            sortDesc,
            filter,
            totalRows: 0
          })
          return []
        }
      ).finally(() => {
        this.tableCustomers.busy = false
      })
    },

    async searchFilterClusters (search, loading) {
      this.searchClustersProvider(this.tableCustomers, search, loading)
    }
  }
}
</script>
